/* Environment resolution for the contractor workspace.
 *
 * Hostname-driven, the same idiom `shoku-partners/public/config.js` and the
 * marketing site's `firebase-init.js` use. A build-time value was deliberately
 * avoided: one artefact is then provably identical across dev and prod, so a
 * promotion cannot smuggle in a different bundle than the one that was tested.
 *
 * Firebase web API keys are NOT secrets — they identify a project and are meant
 * to ship to the browser. Access is governed by Firebase Security Rules and, for
 * this site, by the `userType: "contractor"` claim every API call is checked
 * against.
 */

export type EnvName = "local" | "dev" | "prod";

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
}

export interface AppConfig {
  env: EnvName;
  /** contractor-api origin — profile, jobs, quotes, payouts, bookings. */
  contractorApi: string;
  /** conversation-api origin — conversations, messages, appointments. */
  conversationApi: string;
  /** The homeowner site. One Auth pool, one role per email, so a homeowner who
   *  lands here has to be sent to the other door rather than told to find it. */
  userWebsite: string;
  firebase: FirebaseConfig;
  /** Auth emulator origin, or null when talking to real Firebase Auth. */
  authEmulator: string | null;
  /** Firestore emulator host:port, or null when talking to real Firestore. */
  firestoreEmulator: { host: string; port: number } | null;
  /**
   * Browser key for Places API (New) autocomplete — the `website-api-keys` key
   * in each project, scoped by API target to `places.googleapis.com` alone.
   *
   * ⚠️ NOT yet restricted by HTTP referrer; that waits on the custom domains
   * being live. When it is added, the allowlist must include the local dev
   * origins (`http://localhost:5174/*`, `http://127.0.0.1:5174/*`) or
   * autocomplete stops working for everyone developing against `just
   * contractor` — the picker will disable itself and onboarding becomes
   * impossible to complete locally.
   *
   * Even once restricted, a referrer allowlist is a BILLING control rather than
   * a security one: `Referer` is client-supplied and trivially forged outside a
   * browser. The API-target restriction is what limits the blast radius, by
   * making this key useless for anything but Places.
   *
   * Empty disables the service-area picker outright rather than letting it 403
   * on every keystroke.
   */
  placesApiKey: string;
}

const LOOPBACK = /^(localhost|127\.0\.0\.1|\[::1\])$/;

export const isLocal = LOOPBACK.test(location.hostname);

/* Production — shoku-prod, project number 705547820315.
 *
 * The API origins are the deterministic Cloud Run v2 URLs. They are named
 * explicitly here AND in the firebase.json connect-src: a wildcard such as
 * https://*.run.app would make every Cloud Run service on earth a legal
 * destination for this page's fetches. */
const PROD: AppConfig = {
  env: "prod",
  contractorApi: "https://contractor-api-705547820315.africa-south1.run.app",
  conversationApi: "https://conversation-api-705547820315.africa-south1.run.app",
  userWebsite: "https://shoku-prod-user-website.web.app",
  firebase: {
    // The `contractor-website` web app in shoku-prod
    // (1:705547820315:web:e01f885cd97f5e639cc7be). Firebase shares one browser
    // key per project, so this is the same value the marketing site ships.
    apiKey: "AIzaSyCVt2qLVyvczmsrRWmtqE4INtjxfbnnh2E",
    authDomain: "shoku-prod.firebaseapp.com",
    projectId: "shoku-prod",
  },
  authEmulator: null,
  firestoreEmulator: null,
  placesApiKey: "AIzaSyAs3WyjFZ8eilVXUa4zMwbnTe74yeDrfQI",
};

/* Dev — shoku-dev, project number 955265895737. Served from
 * shoku-dev-contractor-website.web.app, using the `contractor-website` Firebase
 * web app that already exists in that project. */
const DEV: AppConfig = {
  env: "dev",
  contractorApi: "https://contractor-api-955265895737.africa-south1.run.app",
  conversationApi: "https://conversation-api-955265895737.africa-south1.run.app",
  userWebsite: "https://shoku-dev-user-website.web.app",
  firebase: {
    apiKey: "AIzaSyDK5kB8CiJrTPQO77tZbcIeVjQGHbUK5wg",
    authDomain: "shoku-dev.firebaseapp.com",
    projectId: "shoku-dev",
  },
  authEmulator: null,
  firestoreEmulator: null,
  placesApiKey: "AIzaSyDgPUYc5R9wU2Cw9KRqa5CC0B-Tq_eLeuA",
};

/* Local development against docker-compose.
 *
 * projectId MUST be shoku-dev, not the demo-shoku project the Hosting emulator
 * serves this bundle from. The two emulators are separate: the APIs verify ID
 * tokens with the Admin SDK against shoku-dev, and the Auth emulator rejects a
 * token whose audience is a different project — with an error that says nothing
 * about a project mismatch. */
const LOCAL: AppConfig = {
  env: "local",
  contractorApi: "http://127.0.0.1:8083",
  conversationApi: "http://127.0.0.1:8082",
  // The user site's own pinned Vite port; 5174 is this one's.
  userWebsite: "http://127.0.0.1:5173",
  firebase: {
    // The Auth emulator does not validate API keys, so no real key is embedded.
    apiKey: "shoku-local-emulator-key",
    authDomain: "shoku-dev.firebaseapp.com",
    projectId: "shoku-dev",
  },
  authEmulator: "http://127.0.0.1:9099",
  firestoreEmulator: { host: "127.0.0.1", port: 8080 },
  placesApiKey: "AIzaSyDgPUYc5R9wU2Cw9KRqa5CC0B-Tq_eLeuA",
};

function resolve(): AppConfig {
  if (isLocal) return { ...LOCAL };
  if (location.hostname.endsWith("contractor.shoku.co.za")) return { ...PROD };
  // *.web.app / *.firebaseapp.com preview and dev channels.
  if (location.hostname.includes("shoku-prod")) return { ...PROD };
  return { ...DEV };
}

const resolved = resolve();

/* Loopback-only overrides, so the suite can point at a stack running on spare
 * ports without editing this file — the browser equivalent of the Flutter apps'
 * --dart-define=CONTRACTOR_API_BASE_URL. Gated on isLocal, so they are inert on
 * the deployed site even if something manages to set the global. */
declare global {
  interface Window {
    __CONTRACTOR_API__?: string;
    __CONVERSATION_API__?: string;
    __AUTH_EMULATOR__?: string;
    __PLACES_API_KEY__?: string;
  }
}

if (isLocal) {
  if (typeof window.__CONTRACTOR_API__ === "string" && window.__CONTRACTOR_API__) {
    resolved.contractorApi = window.__CONTRACTOR_API__;
  }
  if (typeof window.__CONVERSATION_API__ === "string" && window.__CONVERSATION_API__) {
    resolved.conversationApi = window.__CONVERSATION_API__;
  }
  if (typeof window.__AUTH_EMULATOR__ === "string" && window.__AUTH_EMULATOR__) {
    resolved.authEmulator = window.__AUTH_EMULATOR__;
  }
  if (typeof window.__PLACES_API_KEY__ === "string") {
    resolved.placesApiKey = window.__PLACES_API_KEY__;
  }
}

export const config: Readonly<AppConfig> = Object.freeze(resolved);

/**
 * What is missing from this environment's configuration, if anything.
 *
 * Both values below are filled in by hand, and both fail in ways that point
 * somewhere else entirely:
 *
 *  - An empty `firebase.apiKey` makes every Auth call answer
 *    `auth/invalid-api-key`, which a sign-in form reports as a credentials
 *    problem. A contractor would conclude their password is wrong, forever.
 *  - An empty `placesApiKey` disables the service-area picker, and because
 *    `serviceAreaGeos` is required by `POST /auth/complete`, nobody can finish
 *    onboarding at all.
 *
 * Surfacing this as a explicit configuration error is the difference between a
 * five-minute fix and a support ticket that blames the login form.
 */
export function missingConfiguration(): string[] {
  const missing: string[] = [];
  if (!config.firebase.apiKey) missing.push("Firebase web API key");
  if (!config.placesApiKey) missing.push("Google Places browser key");
  return missing;
}

/** Whether the app can sign anyone in at all. */
export function isAuthConfigured(): boolean {
  return config.firebase.apiKey.length > 0;
}
